const pct = (v: number | null): string => (v === null ? '—' : `${v}%`)
const initials = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
const barColor = (c: number | null): string => ((c ?? 0) < 60 ? '#dc2626' : (c ?? 0) < 80 ? '#ea580c' : '#16a34a')

interface OfficerLeaderboardRowProps {
  name: string
  /** Secondary line by the name — e.g. "3 mandals" (district). Omitted at mandal scope. */
  subtitle?: string
  completed: number
  assigned: number
  missed: number
  completionPct: number | null
  /** When provided, the row becomes a button that opens the officer accountability drawer. */
  onClick?: () => void
}

/**
 * One officer row in an accountability leaderboard — avatar, completion bar, done/assigned · missed.
 * Shared by the district Officers lens (subtitle = "N mandals") and the mandal Officers lens (no subtitle).
 * With `onClick` it renders as a button that opens the officer drawer. Completion colour-codes at 60/80.
 */
export function OfficerLeaderboardRow({ name, subtitle, completed, assigned, missed, completionPct, onClick }: OfficerLeaderboardRowProps) {
  const rowClass = 'flex w-full items-center gap-2.5 border-t border-slate-100 py-2 text-left first:border-t-0'
  const body = (
    <>
      <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-violet-100 text-[12px] font-extrabold text-violet-700">
        {initials(name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-bold">
          {name}
          {subtitle ? <span className="text-muted-text ml-1.5 text-[10px] font-semibold">{subtitle}</span> : null}
        </div>
        <div className="my-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full" style={{ width: `${completionPct ?? 0}%`, background: barColor(completionPct) }} />
        </div>
        <div className="text-muted-text text-[10.5px]">
          {completed}/{assigned} done · <b className={missed > 0 ? 'text-red-600' : ''}>{missed} missed</b>
        </div>
      </div>
      <span className={`flex-none text-[14px] font-extrabold tabular-nums ${(completionPct ?? 0) < 60 ? 'text-red-600' : ''}`}>
        {pct(completionPct)}
      </span>
    </>
  )
  return onClick ? (
    <button type="button" onClick={onClick} className={`${rowClass} hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-blue-600`}>
      {body}
    </button>
  ) : (
    <div className={rowClass}>{body}</div>
  )
}
